import type { AccountType, Transaction, TransactionType } from "../types.js";

export interface BalanceAccount {
  id: number;
  type: AccountType;
  openingBalance: string;
}

export interface BalanceTransaction {
  type: TransactionType;
  amount: string;
  accountId: number | null;
  fromAccountId: number | null;
  toAccountId: number | null;
  transactionDate?: string;
}

export function isLiabilityAccount(type: string): boolean {
  return type === "credit_card" || type === "pay_later";
}

export function toCents(value: string | number): number {
  const normalized = String(value).trim().replace(/,/g, "");
  if (!/^-?\d+(?:\.\d{0,2})?$/.test(normalized)) throw new Error("Invalid money amount");
  const negative = normalized.startsWith("-");
  const unsigned = negative ? normalized.slice(1) : normalized;
  const [whole = "0", fraction = ""] = unsigned.split(".");
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(cents)) throw new Error("Money amount is too large");
  return negative ? -cents : cents;
}

export function fromCents(cents: number): string {
  if (!Number.isSafeInteger(cents)) throw new Error("Cents must be an integer");
  const sign = cents < 0 ? "-" : "";
  const absolute = Math.abs(cents);
  return `${sign}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, "0")}`;
}

export function accountBalanceCents(account: BalanceAccount, transactions: BalanceTransaction[]): number {
  let balance = toCents(account.openingBalance);
  for (const transaction of transactions) {
    const amount = toCents(transaction.amount);
    if (isLiabilityAccount(account.type)) {
      if (transaction.type === "expense" && transaction.accountId === account.id) balance += amount;
      if (transaction.type === "income" && transaction.accountId === account.id) balance -= amount;
      if (transaction.type === "transfer" && transaction.toAccountId === account.id) balance -= amount;
      if (transaction.type === "transfer" && transaction.fromAccountId === account.id) balance += amount;
      continue;
    }

    if (transaction.type === "income" && transaction.accountId === account.id) balance += amount;
    if (transaction.type === "expense" && transaction.accountId === account.id) balance -= amount;
    if (transaction.type === "transfer" && transaction.toAccountId === account.id) balance += amount;
    if (transaction.type === "transfer" && transaction.fromAccountId === account.id) balance -= amount;
  }
  return balance;
}

export function goalContributionCents(transaction: BalanceTransaction, linkedAccountId: number): number {
  if (transaction.type !== "transfer") return 0;
  const amount = toCents(transaction.amount);
  if (transaction.toAccountId === linkedAccountId && transaction.fromAccountId !== linkedAccountId) return amount;
  if (transaction.fromAccountId === linkedAccountId && transaction.toAccountId !== linkedAccountId) return -amount;
  return 0;
}

export function summarizeTransactions(transactions: BalanceTransaction[]) {
  let income = 0;
  let expenses = 0;
  for (const transaction of transactions) {
    if (transaction.type === "income") income += toCents(transaction.amount);
    if (transaction.type === "expense") expenses += toCents(transaction.amount);
  }
  return { income, expenses, difference: income - expenses, cashFlow: income - expenses };
}

function isoDate(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
}

function parseIso(value: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error("Date must use YYYY-MM-DD");
  return new Date(`${value}T00:00:00.000Z`);
}

export function periodBounds(mode: "day" | "week" | "month", selected: string) {
  const date = parseIso(selected);
  if (mode === "day") return { start: selected, end: selected };
  if (mode === "week") {
    const day = date.getUTCDay() || 7;
    const start = new Date(date);
    start.setUTCDate(date.getUTCDate() - day + 1);
    const end = new Date(start);
    end.setUTCDate(start.getUTCDate() + 6);
    return { start: isoDate(start), end: isoDate(end) };
  }
  const start = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  const end = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
  return { start: isoDate(start), end: isoDate(end) };
}

export function inPeriod<T extends Pick<Transaction, "transactionDate">>(items: T[], start: string, end: string) {
  return items.filter((item) => item.transactionDate >= start && item.transactionDate <= end);
}

export function goalProgress(contributions: Array<{ amount: string }>, targetAmount: string) {
  const current = contributions.reduce((sum, item) => sum + toCents(item.amount), 0);
  const target = toCents(targetAmount);
  return {
    current,
    remaining: Math.max(target - current, 0),
    percentage: target > 0 ? Math.max(0, Math.min((current / target) * 100, 100)) : 0
  };
}

export type RecurrenceFrequency = "weekly" | "monthly" | "quarterly" | "yearly" | "custom";

function addMonthsClamped(date: Date, months: number): Date {
  const day = date.getUTCDate();
  const result = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
  const lastDay = new Date(Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)).getUTCDate();
  result.setUTCDate(Math.min(day, lastDay));
  return result;
}

export function monthlyStatementCycle(transactionDate: string, periodStartDate: string, periodEndDate: string, paymentDueDate: string) {
  const purchase = parseIso(transactionDate);
  const periodStartAnchor = parseIso(periodStartDate);
  const periodEndAnchor = parseIso(periodEndDate);
  const dueAnchor = parseIso(paymentDueDate);
  if (periodStartAnchor > periodEndAnchor) throw new Error("Calculation period start must be before its end");
  if (purchase <= periodEndAnchor) return { periodStart: periodStartDate, periodEnd: periodEndDate, paymentDueDate };
  let monthOffset = (purchase.getUTCFullYear() - periodEndAnchor.getUTCFullYear()) * 12 + purchase.getUTCMonth() - periodEndAnchor.getUTCMonth();
  if (purchase > addMonthsClamped(periodEndAnchor, monthOffset)) monthOffset += 1;
  const periodEnd = addMonthsClamped(periodEndAnchor, monthOffset);
  const previousEnd = addMonthsClamped(periodEndAnchor, monthOffset - 1);
  const periodStart = new Date(previousEnd); periodStart.setUTCDate(periodStart.getUTCDate() + 1);
  return { periodStart: isoDate(periodStart), periodEnd: isoDate(periodEnd), paymentDueDate: isoDate(addMonthsClamped(dueAnchor, monthOffset)) };
}

export function payLaterInstallmentDueDates(transactionDate: string, periodStartDate: string, periodEndDate: string, paymentDueDate: string, installmentMonths = 1) {
  if (!Number.isInteger(installmentMonths) || installmentMonths < 1 || installmentMonths > 36) throw new Error("Installments must be between 1 and 36 months");
  const firstDueDate = parseIso(monthlyStatementCycle(transactionDate, periodStartDate, periodEndDate, paymentDueDate).paymentDueDate);
  return Array.from({ length: installmentMonths }, (_, index) => isoDate(addMonthsClamped(firstDueDate, index)));
}

export function payLaterInstallmentPlan(purchase: { transactionDate: string; amount: string; installmentMonths?: number | null }, periodStartDate: string, periodEndDate: string, paymentDueDate: string) {
  const installmentMonths = purchase.installmentMonths ?? 1;
  const dueDates = payLaterInstallmentDueDates(purchase.transactionDate, periodStartDate, periodEndDate, paymentDueDate, installmentMonths);
  const total = toCents(purchase.amount);
  const regularAmount = Math.floor(total / installmentMonths);
  const firstAmount = regularAmount + total % installmentMonths;
  return dueDates.map((dueDate, index) => ({
    dueDate,
    amount: index === 0 ? firstAmount : regularAmount
  })).filter((installment) => installment.amount > 0);
}

export function payLaterStatementTotals(purchases: Array<{ transactionDate: string; amount: string; installmentMonths?: number | null }>, periodStartDate: string, periodEndDate: string, paymentDueDate: string) {
  const totals = new Map<string, number>();
  for (const purchase of purchases) {
    for (const installment of payLaterInstallmentPlan(purchase, periodStartDate, periodEndDate, paymentDueDate)) {
      totals.set(installment.dueDate, (totals.get(installment.dueDate) ?? 0) + installment.amount);
    }
  }
  return [...totals].sort(([left], [right]) => left.localeCompare(right)).map(([dueDate, amount]) => ({ dueDate, amount }));
}

export function remainingPayLaterStatementCents(total: number, payments: Array<{ amount: string }>) {
  return Math.max(total - payments.reduce((sum, payment) => sum + toCents(payment.amount), 0), 0);
}

export function unpaidOccurrenceTotalCents(occurrences: Array<{ amount: string; status: string }>) {
  return occurrences
    .filter((occurrence) => occurrence.status !== "paid" && occurrence.status !== "skipped")
    .reduce((sum, occurrence) => sum + toCents(occurrence.amount), 0);
}

export function generateOccurrenceDates(input: {
  scheduleType: "one_time" | "recurring";
  firstDueDate: string;
  frequency?: RecurrenceFrequency | null;
  intervalValue?: number | null;
  endDate?: string | null;
  horizonDate: string;
}) {
  const first = parseIso(input.firstDueDate);
  const horizon = parseIso(input.horizonDate);
  const hardEnd = input.endDate ? parseIso(input.endDate) : horizon;
  const end = hardEnd < horizon ? hardEnd : horizon;
  if (input.scheduleType === "one_time") return first <= end ? [input.firstDueDate] : [];
  if (!input.frequency) throw new Error("Recurring bills require a frequency");

  const dates: string[] = [];
  const interval = Math.max(1, input.intervalValue ?? 1);
  let occurrence = 0;
  let current = first;
  while (current <= end && dates.length < 520) {
    dates.push(isoDate(current));
    occurrence += 1;
    if (input.frequency === "weekly") current = new Date(first.getTime() + occurrence * 7 * 86_400_000);
    else if (input.frequency === "monthly") current = addMonthsClamped(first, occurrence);
    else if (input.frequency === "quarterly") current = addMonthsClamped(first, occurrence * 3);
    else if (input.frequency === "yearly") current = addMonthsClamped(first, occurrence * 12);
    else current = new Date(first.getTime() + occurrence * interval * 86_400_000);
  }
  return dates;
}
